import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Button from "@/components/ui/Button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuth } from "@/auth/auth-context";
import { userService } from "@/auth/user-service";

const signUpSchema = z
  .object({
    firstName: z.string().min(1, "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
    email: z.string().email("Please enter a valid email address"),
    password: z.string().min(6, "Password must be at least 6 characters"),
    confirmPassword: z.string()
  })
  .refine(data => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"]
  });

type SignUpForm = z.infer<typeof signUpSchema>;

interface SignUpProps {
  onSuccess?: () => void;
  onSwitchToSignIn?: () => void;
}

export const SignUp = ({ onSuccess, onSwitchToSignIn }: SignUpProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const { createUserWithEmailAndPassword, signInWithGoogle, sendEmailVerification } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<SignUpForm>({
    resolver: zodResolver(signUpSchema)
  });

  const handleEmailSignUp = async (data: SignUpForm) => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await createUserWithEmailAndPassword(data.email, data.password);

      const userAttributes = {
        first_name: data.firstName,
        last_name: data.lastName
      };

      await userService.createUser(userAttributes);
      setSuccess("Account created successfully!");

      try {
        await sendEmailVerification();
        console.log("Verification email sent");
      } catch (verificationError) {
        console.warn("Email verification failed:", verificationError);
      }

      onSuccess?.();
    } catch (error) {
      setError(error instanceof Error ? error.message : "Sign up failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await signInWithGoogle();

      const userAttributes = userService.extractUserNameFromAuth();

      if (userAttributes) {
        await userService.createUser(userAttributes);
        setSuccess("Account created successfully!");
      } else {
        throw new Error("Could not extract user information from Google account");
      }

      onSuccess?.();
    } catch (error) {
      setError(error instanceof Error ? error.message : "Google sign up failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-4 text-center">
        <h1 className="text-xl font-semibold text-gray-900">
          <b>Tigim HQ</b> Sign Up
        </h1>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="mb-4">
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      <form
        autoComplete="off"
        onSubmit={handleSubmit(handleEmailSignUp)}
        className="space-y-3"
        aria-label="Sign up form"
      >
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
              First Name
            </label>
            <Input
              id="firstName"
              {...register("firstName")}
              className="w-full h-9"
              required
              disabled={isLoading}
              aria-describedby="firstName-description"
            />
            <div id="firstName-description" className="sr-only">
              Enter your first name
            </div>
            {errors.firstName && (
              <p className="text-sm text-red-600 mt-1">{errors.firstName.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
              Last Name
            </label>
            <Input
              id="lastName"
              {...register("lastName")}
              className="w-full h-9"
              required
              disabled={isLoading}
              aria-describedby="lastName-description"
            />
            <div id="lastName-description" className="sr-only">
              Enter your last name
            </div>
            {errors.lastName && (
              <p className="text-sm text-red-600 mt-1">{errors.lastName.message}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email
          </label>
          <Input
            id="email"
            type="email"
            {...register("email")}
            className="w-full h-9"
            required
            disabled={isLoading}
            aria-describedby="email-description"
          />
          <div id="email-description" className="sr-only">
            Enter your email address
          </div>
          {errors.email && <p className="text-sm text-red-600 mt-1">{errors.email.message}</p>}
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
            Password
          </label>
          <Input
            id="password"
            type="password"
            {...register("password")}
            className="w-full h-9"
            required
            disabled={isLoading}
            autoComplete="new-password"
            aria-describedby="password-description"
          />
          <div id="password-description" className="sr-only">
            Enter a password (minimum 6 characters)
          </div>
          {errors.password && (
            <p className="text-sm text-red-600 mt-1">{errors.password.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
            Confirm Password
          </label>
          <Input
            id="confirmPassword"
            type="password"
            {...register("confirmPassword")}
            className="w-full h-9"
            required
            disabled={isLoading}
            autoComplete="new-password"
            aria-describedby="confirmPassword-description"
          />
          <div id="confirmPassword-description" className="sr-only">
            Confirm your password
          </div>
          {errors.confirmPassword && (
            <p className="text-sm text-red-600 mt-1">{errors.confirmPassword.message}</p>
          )}
        </div>

        <Button
          type="submit"
          className="w-full h-10 text-base bg-purple-600 hover:bg-[#0f172a] text-white"
          disabled={isLoading}
          aria-label="Create account with provided information"
        >
          {isLoading ? "Creating account..." : "Create Account"}
        </Button>

        <p className="text-center text-xs text-gray-500 mt-2" role="note">
          No credit card needed
        </p>

        <div className="relative" aria-label="Alternative signup options">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-xs">
            <span className="px-2 bg-white text-gray-500">or</span>
          </div>
        </div>

        <Button
          type="button"
          variant="outline"
          onClick={handleGoogleSignUp}
          className="w-full h-10 text-base border-gray-300 hover:bg-gray-50"
          disabled={isLoading}
          aria-label="Sign up with Google account"
        >
          <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" aria-hidden="true">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          {isLoading ? "Creating account..." : "Sign up with Google"}
        </Button>
      </form>

      <div className="text-center mt-4 flex flex-col items-center gap-2">
        <Button variant="link" onClick={onSwitchToSignIn} disabled={isLoading}>
          Do you already have an account? Sign In
        </Button>
      </div>
    </div>
  );
};
